import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  handleGetLineageGeometriesGeometryIdLineageGetOptions,
  handleListGeometryMeshesGeometriesGeometryIdMeshesGetOptions,
} from '@/api/generated/@tanstack/react-query.gen';
import type {
  GeometryLineageResponse,
  GeometryStatus,
  MeshResponse,
} from '@/api/generated/types.gen';
import {
  chosenMeshFor,
  geometryMeshingStatusFor,
  geometryReadinessFor,
  lineageGeometryStatus,
  lineagePollInterval,
  type GeometryMeshingStatus,
} from '@/routes/_app/simops/-components/meshing/geometryMeshing';
import { TIMEOUTS } from '../constants';

export interface GeometryMeshStatuses {
  statusByGeomId: Map<string, GeometryMeshingStatus>;
  chosenByGeomId: Map<string, MeshResponse>;
  meshesByGeomId: Map<string, MeshResponse[]>;
  isLoading: boolean;
}

/**
 * For each geometry id, fetches its mesh list and its lineage, and derives the
 * Mesh step's status + chosen (latest-completed) mesh. Polls while a geometry
 * is meshing or still being prepared, so the draft Mesh step updates after
 * the user submits a mesh in the workspace or a repair finishes.
 *
 * `poll: false` for a reader that only follows along: it shares the cache
 * with the page's polling subscriber, and a second observer would run its own
 * timer — out of phase, doubling the requests (ENG-3100).
 */
export function useGeometryMeshStatuses(
  geometryIds: string[],
  { poll = true }: { poll?: boolean } = {},
): GeometryMeshStatuses {
  // Derived by `combine` rather than by a `useMemo` over the results array.
  // The memo this replaces had to SPREAD one dep per query
  // (`...queries.map((q) => q.data)`), and a dep list that is not an array
  // literal is exactly what `react-hooks/use-memo` rejects — its length can
  // change between renders, so React cannot compare it position by position.
  //
  // `combine` is the supported way to say the same thing: QueriesObserver
  // re-runs it only when the results (or `combine` itself) change, so the Maps
  // keep their identity across renders that changed nothing — which is what
  // the spread-dep memo bought. Structural sharing still means a poll that
  // returns an identical payload does not re-run it at all.
  //
  // Keyed on the JOINED ids, not on `geometryIds`: callers build that array
  // inline, so a raw dep would give `combine` a new identity every render and
  // defeat the memo. Reading the ids back out of the key keeps the callback
  // from closing over the stale array.
  const idsKey = geometryIds.join(',');
  const combineMeshes = useCallback(
    (
      results: {
        data?: { items?: MeshResponse[] };
        isLoading: boolean;
        isFetched: boolean;
      }[],
    ) => {
      const chosenByGeomId = new Map<string, MeshResponse>();
      const meshesByGeomId = new Map<string, MeshResponse[]>();
      const loaded = new Set<string>();
      const ids = idsKey === '' ? [] : idsKey.split(',');
      ids.forEach((id, i) => {
        const items = results[i]?.data?.items ?? [];
        meshesByGeomId.set(id, items);
        if (results[i]?.isFetched) loaded.add(id);
        const chosen = chosenMeshFor(items);
        if (chosen) chosenByGeomId.set(id, chosen);
      });
      return {
        chosenByGeomId,
        meshesByGeomId,
        loaded,
        isLoading: results.some((q) => q.isLoading),
      };
    },
    [idsKey],
  );
  // The geometry's own state, read off its lineage (ENG-3100): repair and
  // simplify run on child versions, so the uploaded root the draft holds
  // stays `idle` through all of it. A separate `useQueries`, so a lineage poll
  // leaves the mesh Maps — and everything keyed on their identity — alone.
  const combineLineages = useCallback(
    (
      results: {
        data?: GeometryLineageResponse;
        isLoading: boolean;
        isFetched: boolean;
      }[],
    ) => {
      const statusById = new Map<string, GeometryStatus | undefined>();
      const loaded = new Set<string>();
      const ids = idsKey === '' ? [] : idsKey.split(',');
      ids.forEach((id, i) => {
        statusById.set(id, lineageGeometryStatus(results[i]?.data));
        if (results[i]?.isFetched) loaded.add(id);
      });
      return {
        statusById,
        loaded,
        isLoading: results.some((q) => q.isLoading),
      };
    },
    [idsKey],
  );

  const meshes = useQueries({
    queries: geometryIds.map((id) => ({
      ...handleListGeometryMeshesGeometriesGeometryIdMeshesGetOptions({
        path: { geometry_id: id },
        query: { lineage: true },
      }),
      refetchInterval: (query: {
        state: { data?: { items?: MeshResponse[] } };
      }) =>
        poll &&
        geometryMeshingStatusFor(query.state.data?.items ?? []) === 'meshing'
          ? TIMEOUTS.STATUS_POLL_INTERVAL
          : false,
    })),
    combine: combineMeshes,
  });
  const lineages = useQueries({
    queries: geometryIds.map((id) => ({
      ...handleGetLineageGeometriesGeometryIdLineageGetOptions({
        path: { geometry_id: id },
      }),
      refetchInterval: (query: { state: { data?: GeometryLineageResponse } }) =>
        poll &&
        lineagePollInterval(query.state.data, TIMEOUTS.STATUS_POLL_INTERVAL),
    })),
    combine: combineLineages,
  });

  const statusByGeomId = useMemo(() => {
    const out = new Map<string, GeometryMeshingStatus>();
    for (const [id, items] of meshes.meshesByGeomId) {
      out.set(
        id,
        geometryReadinessFor({
          meshes: items,
          meshesLoaded: meshes.loaded.has(id),
          geometryStatus: lineages.statusById.get(id),
          geometryLoaded: lineages.loaded.has(id),
        }),
      );
    }
    return out;
  }, [meshes, lineages]);

  return {
    statusByGeomId,
    chosenByGeomId: meshes.chosenByGeomId,
    meshesByGeomId: meshes.meshesByGeomId,
    isLoading: meshes.isLoading || lineages.isLoading,
  };
}
